/* Landing-page banner that drives mobile-verified sign-ups via the
 * /free-sample lead-capture flow. Auto-hides when the campaign is
 * disabled from the admin panel.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreeSampleBanner = () => {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/sample-design/config`)
      .then(({ data }) => {
        if (!cancel) setCfg(data);
      })
      .catch(() => {
        if (!cancel) setCfg({ enabled: false });
      });
    return () => {
      cancel = true;
    };
  }, []);

  if (!cfg || !cfg.enabled) return null;

  return (
    <section className="mt-12" data-testid="free-sample-banner">
      <Link
        to="/free-sample"
        data-testid="free-sample-banner-cta"
        className="group block rounded-3xl bg-gradient-to-br from-gold-dark via-gold to-gold-dark text-navy p-5 sm:p-7 border border-gold shadow-[0_30px_60px_-30px_rgba(184,134,11,0.55)] relative overflow-hidden hover:shadow-[0_40px_80px_-30px_rgba(184,134,11,0.65)] transition-all"
      >
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-navy" />
        <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/10 blur-3xl pointer-events-none" />

        <div className="flex items-start gap-4 relative">
          <div className="h-14 w-14 rounded-2xl bg-navy text-gold grid place-items-center shrink-0 shadow-lg">
            <Sparkles className="h-7 w-7" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-navy/10 border border-navy/20 text-navy text-[10.5px] font-bold tracking-wider uppercase">
              <ShieldCheck className="h-3 w-3" />
              100% Free · Mobile-verified
            </div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-balance">
              {cfg.headline}
            </h2>
            <p className="mt-1.5 text-[14.5px] text-navy/85 leading-relaxed max-w-prose">
              {cfg.subtext}
            </p>
            {cfg.design && (
              <div className="mt-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-navy/80">
                <span className="font-extrabold text-navy">Today&apos;s sample:</span>
                <span>{cfg.design.name}</span>
                <span>·</span>
                <span>{cfg.design.size_label} plot</span>
                <span>·</span>
                <span>{cfg.design.bhk} BHK</span>
              </div>
            )}
            <div
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-gold font-extrabold text-[13.5px] group-hover:bg-navy-deep transition-colors"
              data-testid="free-sample-banner-arrow"
            >
              {cfg.cta_label || "Verify mobile to download free sample"}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default FreeSampleBanner;
